import { type CSSProperties, useState } from "react";

import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/cn";

const ROLL_TRIGGER = { once: true, rootMargin: "0px 0px -12% 0px" };

const BASE_CYCLES = 2;
const BASE_DURATION_MS = 900;
const DURATION_STEP_MS = 250;

const isDigit = (char: string) => char >= "0" && char <= "9";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface RollingNumberProps {
  value: string;
  className?: string;
}

/** Перебор цифр. Лента и готовый текст — два слоя: градиентная заливка
 *  не переживает окна цифр с overflow: hidden */
export const RollingNumber = ({ value, className }: RollingNumberProps) => {
  const [ref, inView] = useInView<HTMLSpanElement>(ROLL_TRIGGER);
  const [finishedDigits, setFinishedDigits] = useState(0);

  const digitCount = [...value].filter(isDigit).length;

  if (prefersReducedMotion() || digitCount === 0) {
    return <span className={className}>{value}</span>;
  }

  let digitOrder = 0;

  return (
    <span
      ref={ref}
      className="landing-roll"
      data-done={finishedDigits >= digitCount ? "" : undefined}
      onTransitionEnd={(event) => {
        if (event.propertyName === "transform") setFinishedDigits((count) => count + 1);
      }}
    >
      <span
        aria-hidden
        className={cn("landing-roll-track", className)}
      >
        {[...value].map((char, index) => {
          if (!isDigit(char)) {
            return (
              <span
                key={index}
                className="landing-roll-static"
              >
                {char}
              </span>
            );
          }

          const order = digitOrder++;
          // Нужная цифра стоит в ленте последней
          const offset = (BASE_CYCLES + order) * 10 + Number(char);

          return (
            <span
              key={index}
              className="landing-roll-digit"
              data-rolled={inView ? "" : undefined}
              style={
                {
                  "--roll-offset": offset,
                  "--roll-duration": `${BASE_DURATION_MS + order * DURATION_STEP_MS}ms`,
                } as CSSProperties
              }
            >
              <span className="landing-roll-strip">
                {Array.from({ length: offset + 1 }, (_, position) => (
                  <span key={position}>{position % 10}</span>
                ))}
              </span>
            </span>
          );
        })}
      </span>

      <span className={cn("landing-roll-final", className)}>{value}</span>
    </span>
  );
};
